import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, MessageCircle, Copy, CheckCircle2, Clock, XCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import GeneratePaymentLink from "./GeneratePaymentLink";

const STATUS = {
  pending: { t: "Pending", c: "border-amber-200 bg-amber-50 text-amber-700", Icon: Clock },
  paid: { t: "Paid", c: "border-green-200 bg-green-50 text-green-700", Icon: CheckCircle2 },
  expired: { t: "Expired", c: "border-red-200 bg-red-50 text-red-600", Icon: XCircle },
};
const fmtAmt = (l) => `${(l.currency || "usd").toUpperCase()} ${Number(l.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmt = (t) => { try { return new Date(t).toLocaleDateString(); } catch { return t; } };

export default function AdminPayments() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(null);
  const [genOpen, setGenOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/payment-links"); setLinks(r.data.links || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    all: links.length,
    pending: links.filter((l) => l.status === "pending").length,
    paid: links.filter((l) => l.status === "paid").length,
    expired: links.filter((l) => l.status === "expired").length,
  }), [links]);

  const collected = useMemo(() => {
    const by = {};
    links.filter((l) => l.status === "paid").forEach((l) => {
      const c = (l.currency || "usd").toUpperCase();
      by[c] = (by[c] || 0) + Number(l.amount || 0);
    });
    return by;
  }, [links]);

  const shown = useMemo(() => filter === "all" ? links : links.filter((l) => l.status === filter), [links, filter]);

  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Link copied."); };
  const sendEmail = async (sid) => {
    setSending(sid);
    try { await api.post(`/admin/payment-links/${sid}/send-email`); toast.success("Payment email sent."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email."); } finally { setSending(null); }
  };
  const whatsapp = (l) => {
    const text = encodeURIComponent(`Hi ${l.client_name || "there"}, here is your secure payment link for ${l.service_name} (${fmtAmt(l)}): ${l.url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  return (
    <div className="mt-8" data-testid="admin-payments">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => setGenOpen(true)} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9" data-testid="payments-generate-btn"><Plus className="h-4 w-4 mr-1" /> Generate payment link</Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["all", "Total links", counts.all], ["pending", "Pending", counts.pending], ["paid", "Paid", counts.paid], ["expired", "Expired", counts.expired]].map(([k, l, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4 card-soft" data-testid={`payments-stat-${k}`}>
            <p className="font-heading text-3xl tracking-tight text-foreground">{v}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{l}</p>
          </div>
        ))}
      </div>
      {Object.keys(collected).length > 0 && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="payments-collected">Collected: {Object.entries(collected).map(([c, v]) => `${c} ${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`).join(" · ")}</p>
      )}

      {/* Filters */}
      <div className="mt-5 inline-flex rounded-sm border border-border bg-card p-1">
        {[["all", `All (${counts.all})`], ["pending", `Pending (${counts.pending})`], ["paid", `Paid (${counts.paid})`], ["expired", `Expired (${counts.expired})`]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-sm text-xs transition-colors ${filter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`payments-filter-${k}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>
      ) : shown.length === 0 ? (
        <p className="text-muted-foreground py-16 text-center" data-testid="payments-empty">No payment links found — click “Generate payment link” above to create one.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {shown.map((l) => {
            const s = STATUS[l.status] || STATUS.pending;
            return (
              <div key={l.session_id} className="rounded-xl border border-border bg-card p-4 card-soft" data-testid={`payments-row-${l.session_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-wider ${s.c}`} data-testid={`payments-status-${l.session_id}`}><s.Icon className="h-3 w-3" /> {s.t}</span>
                    <span className="text-sm font-medium text-foreground">{l.service_name}</span>
                    <span className="font-mono text-sm text-primary">{fmtAmt(l)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground truncate">{l.client_name ? `${l.client_name} · ` : ""}{l.client_email}</span>
                </div>
                {l.description && <p className="mt-1 text-xs text-muted-foreground">{l.description}</p>}
                <div className="mt-1 text-[11px] text-muted-foreground">Generated {fmt(l.created_at)}</div>
                <div className="mt-2 flex items-center gap-1.5 rounded-sm border border-border bg-background px-2 py-1">
                  <span className="text-[11px] font-mono text-muted-foreground truncate flex-1">{l.url}</span>
                  <button onClick={() => copy(l.url)} className="text-muted-foreground hover:text-orange-700 shrink-0" data-testid={`payments-copy-${l.session_id}`}><Copy className="h-3.5 w-3.5" /></button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" disabled={sending === l.session_id} onClick={() => sendEmail(l.session_id)} className="h-7 rounded-sm border-slate-300 bg-card text-xs" data-testid={`payments-email-${l.session_id}`}>
                    {sending === l.session_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Mail className="h-3.5 w-3.5 mr-1" /> Email</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => whatsapp(l)} className="h-7 rounded-sm border-green-300 text-green-700 bg-card hover:bg-green-50 text-xs" data-testid={`payments-whatsapp-${l.session_id}`}><MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <GeneratePaymentLink open={genOpen} onOpenChange={setGenOpen} onCreated={load} />
    </div>
  );
}
